import {
  ModusWcSliderCustomEvent,
  ModusWcSwitchCustomEvent,
} from '@trimble-oss/moduswebcomponents';
import { Component, For, Show, createEffect, mergeProps, on } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { PointCloudClassificationControl } from '../../components/point-cloud-classification/point-cloud-classification';
import { PointCloudClearanceControl } from '../../components/point-cloud-clearance/point-cloud-clearance';
import { PointCloudElevationControl } from '../../components/point-cloud-elevation/point-cloud-elevation';
import { PointCloudRgbControl } from '../../components/point-cloud-rgb/point-cloud-rgb';
import {
  PointCloudClassification,
  PointCloudStyling,
  PointCloudStylingBand,
  PointCloudStylingIntensity,
  PointCloudStylingIntensityGradient,
  PointCloudStylingType,
} from '@trimble/types';
import {
  POINT_CLOUD_STYLING_CONTAINER_DEFAULTS,
  PointCloudStylingContainerProps,
} from './point-cloud-styling.model';
import { fixModusSliderStyle } from '../../utils/style-fixes';
import { t } from '@trimble/i18n';

// Constants
const POINT_SIZE_CONFIG = {
  min: 1,
  max: 10,
  step: 1,
} as const;

const STYLING_TYPE_OPTIONS = [
  {
    value: PointCloudStylingType.Rgb,
    label: t('mobileMapping.styles.renderingMode.options.rgb'),
  },
  {
    value: PointCloudStylingType.Classification,
    label: t('mobileMapping.styles.renderingMode.options.classification'),
  },
  {
    value: PointCloudStylingType.Clearance,
    label: t('mobileMapping.styles.renderingMode.options.clearance'),
  },
  {
    value: PointCloudStylingType.Elevation,
    label: t('mobileMapping.styles.renderingMode.options.elevation'),
  },
];

export const PointCloudStylingContainer: Component<
  PointCloudStylingContainerProps
> = (props) => {
  fixModusSliderStyle();

  const activeProps = mergeProps(POINT_CLOUD_STYLING_CONTAINER_DEFAULTS, props);

  const [styling, setStyling] = createStore<PointCloudStyling>({});
  const [classifications, setClassifications] = createStore<
    PointCloudClassification[]
  >([]);

  // Initialize stores from props
  createEffect(() => {
    setStyling(activeProps.styling);
  });

  createEffect(() => {
    setClassifications(reconcile(activeProps.classifications, { key: 'id' }));
  });

  createEffect(
    on(
      () => activeProps.elevationPicked,
      (elevationPicked) => {
        setStyling({
          ...styling,
          elevation: { ...styling.elevation, reference: elevationPicked },
        });
      },
      { defer: true }
    )
  );

  createEffect(() => {
    // Skip the initial effect run when styling is empty
    if (Object.keys(styling).length > 0) {
      props.onStylingChange?.(styling);
    }
  });

  const handleEyeDomeLightingToggle = (
    event: ModusWcSwitchCustomEvent<InputEvent>
  ): void => {
    const enabled = (event.detail.target as HTMLInputElement).checked;

    setStyling((prev) => ({
      ...prev,
      eyeDomeLighting: { ...prev.eyeDomeLighting, enabled },
    }));
  };

  const handlePointSizeChange = (
    event: ModusWcSliderCustomEvent<InputEvent>
  ): void => {
    const size = parseInt((event.detail.target as HTMLInputElement).value, 10);

    setStyling((prev) => ({
      ...prev,
      point: { ...prev.point, size },
    }));
  };

  const handleStylingTypeChange = (
    event: Event & { currentTarget: HTMLSelectElement }
  ): void => {
    const stylingType = event.currentTarget.value as PointCloudStylingType;

    setStyling((prev) => ({
      ...prev,
      stylingType,
    }));
  };

  const handleIntensityChange = (
    intensity: PointCloudStylingIntensity
  ): void => {
    setStyling((prev) => ({
      ...prev,
      intensity,
    }));
  };

  const handleClassificationChange = (
    updatedClassification: PointCloudClassification
  ): void => {
    const index = classifications.findIndex(
      (c) => c.id === updatedClassification.id
    );

    if (index !== -1) {
      setClassifications(index, updatedClassification);

      // Create the class definitions from the updated store
      const updatedClassDefinitions = classifications.map((c) => ({
        id: c.id,
        color: c.color,
        visibility: c.visible,
      }));

      setStyling((prev) => ({
        ...prev,
        classification: {
          ...prev.classification,
          classDefinitions: updatedClassDefinitions,
        },
      }));
    }
  };

  const handleBlendIntensityToggle = (enabled: boolean): void => {
    setStyling((prev) => ({
      ...prev,
      intensity: { ...prev.intensity, blendIntensity: enabled },
    }));
  };

  const handleIntensityGradientChange = (
    gradient: PointCloudStylingIntensityGradient
  ): void => {
    setStyling((prev) => ({
      ...prev,
      intensity: { ...prev.intensity, gradient },
    }));
  };

  const handleClearanceBandsChange = (bands: PointCloudStylingBand[]): void => {
    setStyling((prev) => ({
      ...prev,
      clearance: { ...prev.clearance, bands },
    }));
  };

  const handleClearanceDefaultColorChange = (defaultColor: string): void => {
    setStyling((prev) => ({
      ...prev,
      clearance: { ...prev.clearance, defaultColor },
    }));
  };

  const handleClearanceDefaultTransparentChange = (
    defaultTransparent: boolean
  ): void => {
    setStyling((prev) => ({
      ...prev,
      clearance: { ...prev.clearance, defaultTransparent },
    }));
  };

  const handleElevationReferenceChange = (reference: number): void => {
    setStyling((prev) => ({
      ...prev,
      elevation: { ...prev.elevation, reference },
    }));
  };

  const handleElevationToleranceChange = (tolerance: number): void => {
    setStyling((prev) => ({
      ...prev,
      elevation: { ...prev.elevation, tolerance },
    }));
  };

  const handleElevationOffsetChange = (offset: number): void => {
    setStyling((prev) => ({
      ...prev,
      elevation: { ...prev.elevation, offset },
    }));
  };

  const handleElevationBandCountChange = (bandCount: number): void => {
    setStyling((prev) => ({
      ...prev,
      elevation: { ...prev.elevation, bandCount },
    }));
  };

  const handleElevationPickingToggle = (enabled: boolean): void => {
    props.onElevationPickingToggle?.(enabled);
  };

  return (
    <div class="w-full flex flex-col gap-4">
      {/* Eye Dome Lighting Control */}
      <div class="flex flex-row gap-2">
        <span class="flex flex-1" id="eye-dome-lighting-label">
          {t('mobileMapping.styles.eyeDomeLighting')}
        </span>
        <modus-wc-switch
          aria-labelledby="eye-dome-lighting-label"
          value={styling.eyeDomeLighting?.enabled}
          on:inputChange={handleEyeDomeLightingToggle}
        />
      </div>

      {/* Point Size Control */}
      <div class="flex flex-row gap-2">
        <span class="flex flex-1" id="point-size-label">
          {t('mobileMapping.styles.pointSize')}
        </span>
        <modus-wc-slider
          aria-labelledby="point-size-label"
          value={styling.point?.size}
          min={POINT_SIZE_CONFIG.min}
          max={POINT_SIZE_CONFIG.max}
          step={POINT_SIZE_CONFIG.step}
          class="flex items-center"
          on:inputChange={handlePointSizeChange}
        />
      </div>

      {/* Rendering Mode Select */}
      <div class="flex flex-col gap-1">
        <span class="flex flex-1" id="rendering-mode-label">
          {t('mobileMapping.styles.renderingMode.title')}
        </span>
        <select
          aria-labelledby="rendering-mode-label"
          onChange={handleStylingTypeChange}
        >
          <For each={STYLING_TYPE_OPTIONS}>
            {(option) => (
              <option
                class="select-option"
                value={option.value}
                selected={styling.stylingType === option.value}
              >
                {option.label}
              </option>
            )}
          </For>
        </select>
      </div>

      {/* RGB Component */}
      <Show
        when={
          styling.stylingType === undefined ||
          styling.stylingType === PointCloudStylingType.Rgb
        }
      >
        <PointCloudRgbControl
          blendIntensity={styling.intensity?.blendIntensity}
          gradient={styling.intensity?.gradient}
          onChange={handleIntensityChange}
        />
      </Show>

      {/* Classification Component */}
      <Show when={styling.stylingType === PointCloudStylingType.Classification}>
        <PointCloudClassificationControl
          classifications={classifications}
          blendIntensity={styling.intensity?.blendIntensity}
          onClassificationChange={handleClassificationChange}
          onBlendIntensityToggle={handleBlendIntensityToggle}
          onIntensityGradientChange={handleIntensityGradientChange}
        />
      </Show>

      {/* Clearance Component */}
      <Show when={styling.stylingType === PointCloudStylingType.Clearance}>
        <PointCloudClearanceControl
          bands={styling.clearance?.bands}
          defaultColor={styling.clearance?.defaultColor}
          defaultTransparent={styling.clearance?.defaultTransparent}
          uom={props.uom}
          onBandsChange={handleClearanceBandsChange}
          onDefaultColorChange={handleClearanceDefaultColorChange}
          onDefaultTransparentChange={handleClearanceDefaultTransparentChange}
        />
      </Show>

      {/* Elevation Component */}
      <Show when={styling.stylingType === PointCloudStylingType.Elevation}>
        <PointCloudElevationControl
          reference={styling.elevation?.reference}
          tolerance={styling.elevation?.tolerance}
          offset={styling.elevation?.offset}
          bandCount={styling.elevation?.bandCount}
          elevationPicking={activeProps.elevationPicking}
          elevationPicked={activeProps.elevationPicked}
          uom={props.uom}
          onReferenceChange={handleElevationReferenceChange}
          onToleranceChange={handleElevationToleranceChange}
          onOffsetChange={handleElevationOffsetChange}
          onBandCountChange={handleElevationBandCountChange}
          onElevationPickingToggle={handleElevationPickingToggle}
        />
      </Show>
    </div>
  );
};

export default PointCloudStylingContainer;
